<script setup>
import CountdownBox from '@/components/checkout/atoms/CountdownBox.vue'
import UpsellDiscountFlag from '@/components/checkout/atoms/UpsellDiscountFlag.vue'
import UpsellNutritionalDialog from '@/components/checkout/atoms/UpsellNutritionalDialog.vue'
import { translate } from '@/plugins/i18n.js'
import ApiController from '@/services/ApiController'
import MessageHelper from '@/services/helpers/MessageHelper.js'
import TranslationsHelper from '@/services/helpers/TranslationsHelper'
import { redirectToSuccessPage } from '@/services/helpers/UtilityHelper.js'
import { useAccessibilityStore } from '@/stores/accessibility'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useDisplay } from 'vuetify'

const props = defineProps({
  successData: { type: Object, default: () => ({}) },
  productImage: { type: [String, null], default: null },
  upsellImage: { type: [String, null], default: null },
  placeholderImage: { type: String, default: '' }
})

const display = useDisplay()
const { highContrastEnabled } = storeToRefs(useAccessibilityStore())

const orderDetails = computed(() => props.successData?.order_details ?? {})
const upsell = computed(() => props.successData?.upsell ?? {})
const discount = computed(() => upsell.value?.discount)

const UPSELL_EXPIRATION_TIME_IN_MINUTES = computed(
  () => upsell.value?.expiration_time ?? 15
)

const upsellTargetTimestamp = computed(() => {
  const orderCreatedAt = orderDetails.value?.created_at
  if (!orderCreatedAt) return null

  // Convert created_at to milliseconds
  const orderCreatedTime =
    typeof orderCreatedAt === 'number'
      ? orderCreatedAt * 1000
      : new Date(orderCreatedAt).getTime()
  const expirationTimeMs = UPSELL_EXPIRATION_TIME_IN_MINUTES.value * 60 * 1000
  const expirationTime = orderCreatedTime + expirationTimeMs

  // Check if the timer has already expired
  if (Date.now() >= expirationTime) {
    return null
  }

  return expirationTime
})

const productImgSize = computed(() =>
  display.mdAndUp.value
    ? { width: 150, height: 150 }
    : { width: 100, height: 100 }
)

const upsellImgSize = computed(() =>
  display.mdAndUp.value
    ? { width: 250, height: 250 }
    : { width: 150, height: 150 }
)

const upsellDiscountFlag = computed(() => {
  if (!discount.value) return ''
  const { amount, type } = discount.value
  switch (type) {
    case 0:
      return `${Math.floor(amount)}%`
    case 1:
      return `${Math.floor(amount)}€`
    default:
      return ''
  }
})

const totalAmountRaw = computed(() =>
  Number(orderDetails.value.total_amount ?? 0)
)
const upsellProductPrice = ref(0)
const upsellProductNutriData = ref({})
const isDialogOpen = ref(false)

onMounted(async () => {
  // Check if timer has already expired
  if (!upsellTargetTimestamp.value) {
    isExpired.value = true
  }

  try {
    if (upsell.value?.upsell_product_id) {
      const product = await ApiController.fetchProduct(
        upsell.value?.upsell_product_id,
        upsell.value?.upsell_campaign_id,
        ''
      )
      upsellProductPrice.value = product?.final_price ?? 0
      upsellProductNutriData.value = product?.nutritional_data ?? {}
    }
  } catch (error) {
    console.error('Failed to fetch upsell product:', error)
    upsellProductPrice.value = 0
  }
})

const originalUpsellPrice = computed(() => Number(upsellProductPrice.value))

const discountedUpsellPrice = computed(() => {
  if (!discount.value || !originalUpsellPrice.value)
    return originalUpsellPrice.value
  const { amount, type } = discount.value
  return type === 1
    ? originalUpsellPrice.value - amount
    : originalUpsellPrice.value * (1 - amount / 100)
})

const totalAmount = computed(() =>
  TranslationsHelper.formatCurrency(
    totalAmountRaw.value + originalUpsellPrice.value
  )
)

const upsellSavings = computed(() =>
  TranslationsHelper.formatCurrency(
    totalAmountRaw.value + discountedUpsellPrice.value
  )
)

const pendingUpsellOrder = ref(false)
const isExpired = ref(false)

const onCountdownExpired = () => {
  isExpired.value = true
}

const placeUpsellOrder = async () => {
  pendingUpsellOrder.value = true
  try {
    const {
      data: { redirect_url: redirectUrl }
    } = await ApiController.placeUpsellOrder(
      props.successData.order_increment_id,
      upsell.value.id
    )
    await redirectToSuccessPage(redirectUrl)
  } catch (error) {
    MessageHelper.addErrorMessage(
      translate('checkout.error.placeOrder'),
      null,
      5000,
      error,
      'checkout_upsell'
    )
  } finally {
    pendingUpsellOrder.value = false
  }
}
</script>

<template>
  <div class="checkout-upsell mt-4 mt-md-12 align-center w-100">
    <div class="pb-4">
      <div class="d-flex flex-column bg-light-ochre pa-3 text-center">
        <span
          class="font-weight-bold text-primary font-garbata font-size-md-xxxxl font-size-xl"
        >
          {{ upsell.catch_phrase }}
        </span>
        <span class="font-size-md-xl font-size-lg" v-html="upsell.reason_why" />
      </div>

      <CountdownBox
        class="mt-4"
        :minutes="UPSELL_EXPIRATION_TIME_IN_MINUTES"
        :target-timestamp="upsellTargetTimestamp"
        @expired="onCountdownExpired"
      />

      <div class="d-flex justify-center align-center">
        <div class="d-flex align-center">
          <div class="product-image-wrapper position-relative d-inline-block">
            <VImg
              :alt="translate('aria_label_success_product')"
              :lazy-src="placeholderImage"
              :src="productImage"
              itemprop="image"
              :width="productImgSize.width"
              :height="productImgSize.height"
              class="product-image opacity-60"
              :class="{ 'high-contrast': highContrastEnabled }"
            />
            <div
              class="d-flex align-center justify-center position-absolute z-2"
              style="inset: 0"
            >
              <VIcon size="40" class="bg-white rounded-circle pa-2"
                >custom:check</VIcon
              >
            </div>
          </div>

          <span class="font-size-xxxxxl font-weight-bold">+</span>
          <div>
            <div
              class="position-relative"
              :style="{
                width: upsellImgSize.width + 'px',
                height: upsellImgSize.height + 'px'
              }"
            >
              <VImg
                :alt="translate('aria_label_upsell_product')"
                :lazy-src="placeholderImage"
                :src="upsellImage"
                itemprop="image"
                class="product-image"
                :width="upsellImgSize.width"
                :height="upsellImgSize.height"
                :class="{ 'high-contrast': highContrastEnabled }"
              />
              <UpsellDiscountFlag
                class="position-absolute top-0 right-0 mt-n4 mr-n4"
                :value="upsellDiscountFlag"
              />
            </div>
            <button
              class="font-size-s text-primary hover:underline"
              @click="isDialogOpen = true"
            >
              {{ translate('cms_tiles_food_claim') }}
            </button>
          </div>
        </div>
      </div>

      <div
        class="upsell-price pt-4 pb-md-8 pb-4 d-flex flex-md-row flex-column align-center justify-sm-center justify-space-between"
      >
        <div
          class="d-flex flex-column font-size-md-l font-size-m text-center text-md-start text-md-end px-md-5 px-2 font-weight-bold"
        >
          <div>{{ translate('after_sale_upsell_total_price') }}</div>
          <div>{{ translate('after_sale_upsell_cta_button_disclaimer') }}:</div>
        </div>
        <div
          class="font-size-xl font-size-md-xxxxxl px-0 px-md-3 font-weight-bold text-center align-center"
        >
          <span v-html="upsellSavings" />
        </div>
        <div
          class="font-size-md-xxxxl font-size-l font-weight-light text-grey text-center align-center text-decoration-line-through"
        >
          <span v-html="totalAmount" />
        </div>
      </div>

      <VBtn
        class="py-4"
        @click="placeUpsellOrder"
        :disabled="pendingUpsellOrder || isExpired"
        :loading="pendingUpsellOrder"
      >
        {{ translate('checkout:upsell_button') }}
      </VBtn>
    </div>
  </div>
  <UpsellNutritionalDialog
    v-model="isDialogOpen"
    :nutritionalData="upsellProductNutriData"
  />
</template>
